/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { withRouter } from "../_shared/withRouter";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import LearnerAuthShell from "./learnerAuthShell";
import InputComponent from "../template/_common/inputComponent";
import LanguageSelector from "../template/_common/languageSelector";
import { globalAlert } from "../actions/commonActions";
import { SUPPORTED_LANGUAGES } from "../i18n/languages";
import { emailShapeOk, resolveLmsShortName, learnerPath } from "./_learnerConfig";
import { learnerLoginApi } from "./_learnerActions";
import { useAuth, clearAuth } from "./learnerAuth";

// Learner login. Success persists data.tokens { ecAuthToken, ecSecureToken } and
// data.learner, then lands on the dashboard. App-status 204 messages (invalid
// credentials / inactive account / temporary lock) render inline, never as a
// fatal toast.
const LearnerLogin = (props) => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const banner = props?.location?.state?.banner;

  // Landing on login means no valid session — clear any stale tokens.
  useEffect(() => {
    // Do not destroy an active admin/user session merely because that person
    // opened a learner login URL in another tab. Learner or legacy sessions are
    // still cleared so stale learner credentials cannot leak into a new login.
    const portalRole = localStorage.getItem("portalRole");
    if (!portalRole || portalRole === "learner") clearAuth();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErrors({});
    let newErrors = {};
    if (!emailShapeOk(email)) {
      newErrors.email = t("learner.lms.errors.emailInvalid", "Enter a valid email address.");
    }
    if (!password) {
      newErrors.password = t("learner.lms.errors.passwordRequired", "Enter your password.");
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    setBusy(true);
    try {
      const res = await learnerLoginApi({ email: email.trim(), password });

      const { status, data, message } = res?.data || {};
      if (status === 200) {
        if (!data?.tokens?.ecAuthToken) {
          setErrors({ general: t("learner.lms.errors.loginFailed", "Unable to start your session. Please try again.") });
          return;
        }
        login({ tokens: data?.tokens, learner: data?.learner });
        const lmsShortName = resolveLmsShortName();
        const from = props?.location?.state?.from || learnerPath(lmsShortName, "dashboard");
        props?.history?.replace(from);

      } else if (status === 204) {
        setErrors({ general: message || t("learner.lms.errors.loginFailed", "Invalid email or password.") });
      } else {
        globalAlert("error", message);
      }
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        (err?.code === "ERR_NETWORK"
          ? t("learner.lms.errors.serviceUnavailable", "The learning service is unavailable. Please try again shortly.")
          : t("learner.lms.errors.loginFailed", "Unable to log in. Please try again."));
      setErrors({ general: message });
      if (err?.response) globalAlert("error", message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <LearnerAuthShell
      title={t("learner.lms.login.title", "Log in")}
      heading={t("learner.lms.login.heading", "Welcome back")}

      subheading={t("learner.lms.login.sub", "Log in to continue your learning.")}
      headingExtra={
        // <LanguageSelector
        //   variant="dark"
        //   languages={SUPPORTED_LANGUAGES}
        //   value={localStorage.getItem("userLanguage") || "en"}
        //   label={t("common.selectLanguage", "Select Language")}
        // />
        null
      }
      organisation={props?.portalInfo?.organisation}
      footer={
        <span style={{ fontSize: '16px' }}>
          {t("learner.lms.login.noAccount", "New here?")}{" "}
          <Link to={learnerPath(resolveLmsShortName(), "register")} className="lms-auth-link text-decoration-none" style={{ color: '#d7eff5', fontSize: '16px' }}>
            {t("learner.lms.register.title", "Create your account")}
          </Link>
        </span>
      }
    >
      {banner && <div className="lms-auth-banner success">{banner}</div>}
      <form onSubmit={submit} className="form-own lms-auth-form" noValidate>
        <div className="form-group reg-date login-page">
          <InputComponent
            placeholder={t("user.login.emailPlaceholder", "Email")}
            label={t("user.login.emailPlaceholder", "Email")}
            name="email"
            type="text"
            keyType="email"
            theme="primary"
            color="#d7eff5"
            maxLength={40}
            value={email}
            autoComplete="email"
            handleCallback={(val) => {
              setEmail(val);
              if (errors.email || errors.general) setErrors({ ...errors, email: "", general: "" });
            }}
          />
          {errors.email && (
            <div id="login-email-err" className="error-text font-12 text-start pl-2 mt-1" role="alert">
              {errors.email}
            </div>
          )}
        </div>
        <div className="form-group reg-date login-page position-relative">
          <InputComponent
            placeholder={t("user.login.passwordPlaceholder", "Password")}
            label={t("user.login.passwordPlaceholder", "Password")}
            name="password"
            type={showPw ? "text" : "password"}
            theme="primary"
            color="#d7eff5"
            maxLength={30}
            value={password}
            autoComplete="off"
            handleCallback={(val) => {
              setPassword(val);
              if (errors.password || errors.general) setErrors({ ...errors, password: "", general: "" });
            }}
          />
          <button
            type="button"
            className="login-password lms-password-toggle cursor"
            aria-label={showPw ? t("learner.lms.hidePassword", "Hide password") : t("learner.lms.showPassword", "Show password")}
            aria-pressed={showPw}
            onClick={() => setShowPw((v) => !v)}
          >
            <i className={`fas ${showPw ? "fa-eye" : "fa-eye-slash"}`} aria-hidden="true"></i>
          </button>
          {errors.password && (
            <div id="login-password-err" className="error-text font-12 text-start pl-2 mt-1" role="alert">
              {errors.password}
            </div>
          )}
        </div>

        {errors.general && (
          <div id="login-err" className="error-text font-12 text-center mb-2" role="alert">
            {errors.general}
          </div>
        )}

        <button
  type="submit"
  className="btn primary-btn btn-block lms-auth-submit w-100"
  disabled={busy}
  aria-label={
    busy
      ? t("learner.lms.login.loggingIn", "Logging in")
      : t("learner.lms.login.button", "Log in")
  }
>
  {busy ? (
    <i className="fas fa-circle-notch fa-spin" aria-hidden="true"></i>
  ) : (
    t("learner.lms.login.button", "Log in")
  )}
</button>

        <div className="text-center mt-3" style={{ fontSize: '16px' }}>
          <Link to={learnerPath(resolveLmsShortName(), "forgot-password")} className="lms-auth-link text-decoration-none" style={{ color: '#d7eff5', fontSize: '16px' }}>
            {t("learner.lms.login.forgot", "Forgot your password?")}
          </Link>
        </div>
      </form>
    </LearnerAuthShell>
  );
};

export default withRouter(LearnerLogin);
